import { RefundModel } from 'api-hooks/refund/refund';
import { Option } from 'baseui/select';
import Separator from 'components/common/separator';
import { useFormState } from 'components/elements/form';
import TableInput from 'components/elements/table/table-input';
import { ColumnDataType, IColumn } from 'components/elements/table/type';
import { HeaderCheckbox } from 'legacy-modules/sales/reschedule/header-checkbox';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import GuestName from '../../guest-name-column';

interface Props {
  refund?: RefundModel;
  parentIndex: number;
}

function reducer(state, action, { update, clearErrors, name, setValue }) {
  switch (action.type) {
    case 'update_cell':
      return {
        ...state,
        data: state.data.map((row, index) => {
          if (index === action.rowIndex) {
            const tempValue = {
              ...state.data[action.rowIndex],
              [action.columnId]: action.value,
              // ...otherVal(),
            };

            // update(index, tempValue);
            clearErrors(name);
            setValue(`${name}.${index}`, tempValue, { shouldValidate: true });
            return tempValue;
          }
          return row;
        }),
      };
    case 'update_all':
      return {
        ...state,
        data: action.newData,
      };
    case 'isSelected':
      return {
        ...state,
        data: state.data.map((row, index) => {
          const tempValue = {
            ...state.data[index],
            isSelected: action.value,
          };
          clearErrors(name);
          // update(index, tempValue);
          setValue(`${name}.${index}`, tempValue, { shouldValidate: true });
          return tempValue;
        }),
      };
    default:
      return state;
  }
}

export const _columns: (props: {
  t: any;
  cruiseRoomList: Option[];
  name: string;
  dispatch;
}) => IColumn<any>[] = ({ t, cruiseRoomList, dispatch, name }) => [
  {
    header: () => <HeaderCheckbox name={name} dispatch={dispatch} />,
    accessorKey: 'isSelected',
    minSize: 50,
    size: 50,
    checkboxNoLabel: true,
    dataType: ColumnDataType.checkbox,
  },
  {
    header: t('common:guest'),
    accessorKey: 'guestName',
    minSize: 150,
    dataType: ColumnDataType.null,
    disabled: true,
    cell: ({ row }) => {
      return <GuestName value={row.original.guestName} />;
    },
  },
  {
    header: t('cruise:cabin'),
    accessorKey: 'cruiseRoomId',
    minSize: 180,
    maxSize: 200,
    dataType: ColumnDataType.select,
    options: cruiseRoomList,
    disabled: true,
  },
  {
    header: t('cruise:cabin_number'),
    accessorKey: 'cruiseRoomNumber',
    minSize: 180,
    maxSize: 200,
    dataType: ColumnDataType.text,
    disabled: true,
  },
  {
    header: t('cruise:deck'),
    accessorKey: 'deck',
    minSize: 180,
    maxSize: 200,
    dataType: ColumnDataType.text,
    disabled: true,
  },
  {
    header: t('ticketSales:basic_fare'),
    accessorKey: 'basicFare',
    minSize: 150,
    maxSize: 150,
    dataType: ColumnDataType.number,
    minNumber: 0,
    disabled: true,
  },
  {
    header: t('ticketSales:port_charge'),
    accessorKey: 'portCharge',
    minSize: 150,
    maxSize: 150,
    dataType: ColumnDataType.number,
    minNumber: 0,
    disabled: true,
  },
  {
    header: t('ticketSales:tax'),
    accessorKey: 'tax',
    minSize: 150,
    maxSize: 150,
    dataType: ColumnDataType.number,
    minNumber: 0,
    disabled: true,
  },
  {
    header: t('ticketSales:tipping'),
    accessorKey: 'tipping',
    minSize: 150,
    maxSize: 150,
    dataType: ColumnDataType.number,
    minNumber: 0,
    disabled: true,
  },
  {
    header: t('cruise:reservation_number'),
    accessorKey: 'description',
    minSize: 150,
    dataType: ColumnDataType.text,
    disabled: true,
  },
  {
    header: t('common:refund_price'),
    accessorKey: 'refundPrice',
    minSize: 100,
    dataType: ColumnDataType.number,
    minNumber: 0,
  },
];

export default function GuestCruiseTable(props: Props) {
  const { t } = useTranslation();
  const { editable } = useFormState();

  const {
    control,
    formState: { isSubmitting },
    clearErrors,
    setValue,
  } = useFormContext();

  const name: any = `cruiseSales.${props.parentIndex}.cruiseSaleDetails`;

  const [isInitiate, setIsInitiate] = React.useState<{
    value: boolean;
    data: any;
  }>({ value: false, data: undefined });

  const { fields, update } = useFieldArray({
    control,
    name,
    keyName: 'customId',
  });

  const [cruiseSales] = useWatch({
    control,
    name: ['cruiseSales'],
  });

  const columns = (dispatch?: any) =>
    _columns({ t, cruiseRoomList: cruiseSales[0].cruiseRooms, dispatch, name });

  const [state, dispatch] = React.useReducer(
    (state, action) =>
      reducer(state, action, { update, clearErrors, name, setValue }),
    {
      columns: columns(),
      data: [],
    },
  );

  React.useEffect(() => {
    if (!isInitiate.value) {
      dispatch({
        type: 'update_all',
        newData: fields.length ? fields : [],
      });
      setIsInitiate({
        value: true,
        data: fields.length ? fields : [],
      });
    }
  }, [fields, isInitiate]);

  React.useEffect(() => {
    if (!editable && isInitiate.value && !isSubmitting) {
      dispatch({ type: 'update_all', newData: isInitiate.data });
    }
  }, [editable, isInitiate.value, isSubmitting, t, isInitiate.data]);

  if (fields.length === 0) {
    return null;
  }

  return (
    <>
      <Separator gap={24} />
      <div style={{ width: '100%' }}>
        <TableInput
          columns={columns(dispatch)}
          data={state.data}
          dispatch={dispatch}
          disabled={!editable || !!props.refund}
          name={name}
          uniqueRowKey="customId"
          onCustomDisabledCell={({ accessorKey, rowOriginal }) =>
            !!(accessorKey !== 'isSelected' && !rowOriginal.isSelected)
          }
          noAddRemoveRow
        />
      </div>
    </>
  );
}
